// Aviso de cookies e análise de uso (LGPD). Montado uma vez no RootLayout
// (src/router.tsx), então aparece em qualquer rota, inclusive nas públicas
// (/auth, /signup, /terms, /privacy). Discreto: um cartão no canto, que não
// bloqueia a tela. Sem escolha, nada de análise carrega (lib/analytics-loader.ts).
//
// Reaberto pelo "Preferências de cookies" (menu do usuário e rodapé das páginas
// legais), mostra a escolha atual e deixa trocar.
import { Link } from "@tanstack/react-router";
import { Cookie } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  closeConsentPreferences,
  openConsentPreferences,
  saveConsent,
  useConsent,
} from "./lib/consent.js";

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { consent, preferencesOpen } = useConsent();
  if (consent && !preferencesOpen) return null;

  return (
    <section
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-3 bottom-3 z-50 sm:right-auto sm:bottom-4 sm:left-4 sm:w-full sm:max-w-sm"
    >
      <Card size="sm" className="shadow-lg">
        <CardHeader>
          <CardTitle id="cookie-consent-title" className="flex items-center gap-2">
            <Cookie className="size-4 text-muted-foreground" aria-hidden />
            {t("cookieConsent.title")}
          </CardTitle>
          <CardDescription className="space-y-1.5 text-xs leading-5">
            <span className="block">
              {t("cookieConsent.description")}{" "}
              <Link to="/privacy" className="underline transition-colors hover:text-foreground">
                {t("cookieConsent.privacyLink")}
              </Link>
              .
            </span>
            {consent ? (
              <span className="block font-medium text-foreground">
                {consent.analytics === "granted"
                  ? t("cookieConsent.currentGranted")
                  : t("cookieConsent.currentDenied")}
              </span>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-end gap-2">
          {consent ? (
            <Button variant="ghost" size="sm" onClick={closeConsentPreferences}>
              {t("cookieConsent.close")}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => saveConsent("denied")}>
            {t("cookieConsent.reject")}
          </Button>
          <Button size="sm" onClick={() => saveConsent("granted")}>
            {t("cookieConsent.accept")}
          </Button>
        </CardFooter>
      </Card>
    </section>
  );
}

// Link de texto para reabrir o aviso (rodapé das páginas legais).
export function CookiePreferencesLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={openConsentPreferences}
      className={
        className ??
        "text-sm text-muted-foreground underline transition-colors hover:text-foreground"
      }
    >
      {t("cookieConsent.preferences")}
    </button>
  );
}
